import type { EligiblePanel, PendingLogin } from '@/types/auth';

// ─── Login a medio terminar (CORP-546) ────────────────────────────────────────
// Cuando el usuario pertenece a 2+ paneles, el login se autentica pero NO deja
// sesión: queda pendiente hasta que elija a dónde entrar. Esto guarda ese estado
// intermedio mientras dura.
//
// Único módulo que conoce las keys. Son funciones puras (sin React) porque quien
// lo escribe es `signIn` en `AuthProvider.js`, que no puede llamar a un hook.

// ⚠️ El token del login pendiente NO puede vivir en la key `token`: `ApiRest` la
// lee en su constructor y la manda como `Authorization` en toda llamada del
// panel. Va en `sessionStorage` porque caduca en 5 minutos y no tiene ningún
// sentido que sobreviva al cierre de la pestaña.
const TOKEN_KEY = 'pendingLoginToken';
const PANELS_KEY = 'pendingLoginPanels';
const EMAIL_KEY = 'pendingLoginEmail';
const ISSUED_AT_KEY = 'pendingLoginIssuedAt';

/** El backend expira el `panelSelectionToken` a los 5 minutos. */
export const PENDING_LOGIN_TTL_MS = 5 * 60 * 1000;

export const savePendingLogin = (
  pending: Omit<PendingLogin, 'issuedAt'>,
): void => {
  window.sessionStorage.setItem(TOKEN_KEY, pending.token);
  window.sessionStorage.setItem(PANELS_KEY, JSON.stringify(pending.panels));
  window.sessionStorage.setItem(EMAIL_KEY, pending.email);
  window.sessionStorage.setItem(ISSUED_AT_KEY, String(Date.now()));
};

export const readPendingLogin = (): PendingLogin | null => {
  const token = window.sessionStorage.getItem(TOKEN_KEY);
  const rawPanels = window.sessionStorage.getItem(PANELS_KEY);
  if (!token || !rawPanels) return null;

  try {
    const panels: unknown = JSON.parse(rawPanels);
    if (!Array.isArray(panels) || panels.length === 0) return null;

    return {
      // Aserción explícita: acá termina lo que TypeScript puede garantizar. Lo
      // que hay en `sessionStorage` lo escribió `savePendingLogin`, no la API.
      panels: panels as EligiblePanel[],
      token,
      email: window.sessionStorage.getItem(EMAIL_KEY) ?? '',
      issuedAt: Number(window.sessionStorage.getItem(ISSUED_AT_KEY) ?? 0),
    };
  } catch {
    // `sessionStorage` manipulado a mano: se trata como "no hay login pendiente".
    return null;
  }
};

export const clearPendingLogin = (): void => {
  window.sessionStorage.removeItem(TOKEN_KEY);
  window.sessionStorage.removeItem(PANELS_KEY);
  window.sessionStorage.removeItem(EMAIL_KEY);
  window.sessionStorage.removeItem(ISSUED_AT_KEY);
};

/**
 * Guarda de reloj local previa a `activate-panel`.
 *
 * Existe para poder **explicarle al usuario** por qué volvió al login: el 401
 * real lo resuelve `ApiRest.handleError` con un `window.location.replace('/')`
 * que descarga la página antes de que se pueda mostrar nada.
 */
export const isPendingLoginExpired = (pending: PendingLogin): boolean =>
  Date.now() - pending.issuedAt >= PENDING_LOGIN_TTL_MS;
